import React, { useState, useEffect, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import {
  Plus,
  Search,
  Filter,
  Download,
  Eye,
  Send,
  FileText,
  Calendar,
  AlertCircle,
  ChevronDown,
  ChevronRight
} from 'lucide-react';
import toast from 'react-hot-toast';
import {
  Button,
  SearchBar,
  DataTable,
  StatusBadge,
  LoadingSpinner
} from '../../components/common';
import { Navigation } from '../../components/layouts';
import { ContractForm } from '../../components/forms';
import Pagination from '../../components/common/Pagination';
import {
  contractService,
  clientService,
  serviceService,
  templateService
} from '../../services';
import { useNavigate } from 'react-router-dom';
import { usePagination } from '../../hooks/usePagination';

const ContractsPage = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showContractForm, setShowContractForm] = useState(false);
  const [editingContract, setEditingContract] = useState(null);
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortDirection, setSortDirection] = useState('desc');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedYear, setSelectedYear] = useState('all');
  const [selectedCounty, setSelectedCounty] = useState('all');
  const [expandedYears, setExpandedYears] = useState({});

  // Fetch contracts using React Query
  const {
    data: contracts = [],
    isLoading: contractsLoading,
    error: contractsError
  } = useQuery(
    'contracts',
    contractService.getAll,
    {
      refetchInterval: 300000,
      onError: (error) => {
        console.error('Failed to load contracts:', error);
        toast.error('Eroare la încărcarea contractelor');
      }
    }
  );

  // Fetch clients
  const {
    data: clients = [],
    isLoading: clientsLoading
  } = useQuery('clients', clientService.getAll, {
    onError: (error) => {
      console.error('Failed to load clients:', error);
      toast.error('Eroare la încărcarea clienților');
    }
  });

  // Fetch services
  const {
    data: services = [],
    isLoading: servicesLoading
  } = useQuery('services', serviceService.getAll, {
    onError: (error) => {
      console.error('Failed to load services:', error);
      toast.error('Eroare la încărcarea serviciilor');
    }
  });

  // Fetch templates
  const {
    data: templates = [],
    isLoading: templatesLoading
  } = useQuery('templates', templateService.getActive, {
    onError: (error) => {
      console.error('Failed to load templates:', error);
      toast.error('Eroare la încărcarea template-urilor');
    }
  });

  // Filter contracts based on search, status, year and county
  const filteredContracts = useMemo(() => {
    return contracts.filter(contract => {
      const matchesSearch = 
        contract.number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        contract.clientName?.toLowerCase().includes(searchTerm.toLowerCase());

      // Match by statusText which contains Romanian labels
      const matchesStatus = 
        statusFilter === 'all' ||
        (contract.statusText && contract.statusText.toLowerCase() === statusFilter.toLowerCase());

      const contractYear = new Date(contract.createdAt).getFullYear().toString();
      const matchesYear = selectedYear === 'all' || contractYear === selectedYear;

      // Get county from client data
      const client = clients.find(c => c.id === contract.clientId);
      const contractCounty = client?.county || 'Necunoscut';
      const matchesCounty = selectedCounty === 'all' || contractCounty === selectedCounty;

      return matchesSearch && matchesStatus && matchesYear && matchesCounty;
    });
  }, [contracts, searchTerm, statusFilter, selectedYear, selectedCounty, clients]);

  // Get available years from contracts
  const availableYears = useMemo(() => {
    const years = [...new Set(contracts.map(c => 
      new Date(c.createdAt).getFullYear()
    ))].sort((a, b) => b - a);
    return years;
  }, [contracts]);

  // Get available counties from clients
  const availableCounties = useMemo(() => {
    const counties = [...new Set(clients.map(c => c.county).filter(Boolean))].sort();
    return counties;
  }, [clients]);

  // Group contracts by year and county
  const groupedContracts = useMemo(() => {
    const grouped = {};
    
    filteredContracts.forEach(contract => {
      const year = new Date(contract.startDate).getFullYear();
      const client = clients.find(c => c.id === contract.clientId);
      const county = client?.county || 'Necunoscut';
      
      if (!grouped[year]) {
        grouped[year] = {};
      }
      if (!grouped[year][county]) {
        grouped[year][county] = [];
      }
      grouped[year][county].push(contract);
    });
    
    return grouped;
  }, [filteredContracts, clients]);

  // Use pagination hook
  const {
    currentPage,
    pageSize,
    totalPages,
    startItem,
    endItem,
    currentItems,
    hasNextPage,
    hasPreviousPage,
    goToPage,
    goToNextPage,
    goToPreviousPage,
    changePageSize,
    pageSizeOptions
  } = usePagination(filteredContracts, {
    initialPageSize: 10,
    pageSizeOptions: [5, 10, 25, 50, 100]
  });

  // Mutations
  const createContractMutation = useMutation(
    contractService.create,
    {
      onSuccess: () => {
        queryClient.invalidateQueries('contracts');
        toast.success('Contract creat cu succes');
        setShowContractForm(false);
        setEditingContract(null);
      },
      onError: (error) => {
        console.error('Failed to create contract:', error);
        toast.error('Eroare la crearea contractului');
      }
    }
  );

  const updateContractMutation = useMutation(
    ({ id, data }) => contractService.update(id, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('contracts');
        toast.success('Contract actualizat cu succes');
        setShowContractForm(false);
        setEditingContract(null);
      },
      onError: (error) => {
        console.error('Failed to update contract:', error);
        toast.error('Eroare la actualizarea contractului');
      }
    }
  );

  const generatePdfMutation = useMutation(
    contractService.generatePdf,
    {
      onSuccess: () => {
        queryClient.invalidateQueries('contracts');
        toast.success('PDF generat cu succes');
      },
      onError: (error) => {
        console.error('Failed to generate PDF:', error);
        toast.error('Eroare la generarea PDF-ului');
      }
    }
  );

  const sendContractMutation = useMutation(
    ({ id, emails }) => contractService.sendContract(id, emails),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('contracts');
        toast.success('Contract trimis cu succes');
      },
      onError: (error) => {
        console.error('Failed to send contract:', error);
        toast.error('Eroare la trimiterea contractului');
      }
    }
  );

  const downloadContractMutation = useMutation(
    contractService.downloadPdf,
    {
      onSuccess: (result) => {
        const { blob, filename } = result;
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        toast.success('Contract descărcat cu succes');
      },
      onError: (error) => {
        console.error('Failed to download contract:', error);
        toast.error('Eroare la descărcarea contractului');
      }
    }
  );

  const handleContractSubmit = async (contractData) => {
    try {
      if (editingContract) {
        await updateContractMutation.mutateAsync({
          id: editingContract.id,
          data: contractData
        });
      } else {
        await createContractMutation.mutateAsync(contractData);
      }
    } catch (error) {
      // Error handled in mutation callbacks
    }
  };

  const handleGeneratePdf = async (contractId) => {
    try {
      await generatePdfMutation.mutateAsync(contractId);
    } catch (error) {
      // Error handled in mutation callbacks
    }
  };

  const handleSendContract = async (contractId) => {
    try {
      const contract = contracts.find(c => c.id === contractId);
      if (!contract) {
        toast.error('Contract nu a fost găsit');
        return;
      }
      const emails = [contract.clientEmail];
      await sendContractMutation.mutateAsync({ id: contractId, emails });
    } catch (error) {
      // Error handled in mutation callbacks
    }
  };

  const handleViewContract = (contract) => {
    navigate(`/contracts/${contract.id}`);
  };

  const handleDownloadContract = async (contract) => {
    try {
      await downloadContractMutation.mutateAsync(contract.id);
    } catch (error) {
      // Error handled in mutation callbacks
    }
  };

  const handleSort = (column, direction) => {
    setSortBy(column);
    setSortDirection(direction);
  };

  const handleExportContracts = async () => {
    try {
      const blob = await contractService.exportToExcel();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `contracte_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Export realizat cu succes');
    } catch (error) {
      console.error('Failed to export contracts:', error);
      toast.error('Eroare la exportul contractelor');
    }
  };

  const toggleYearExpansion = (year) => {
    setExpandedYears(prev => ({
      ...prev,
      [year]: !prev[year]
    }));
  };

  const columns = [
    {
      key: 'number',
      title: 'Număr Contract',
      sortable: true,
      render: (value, contract) => (
        <div>
          <div className="text-sm font-medium text-gray-900">{value}</div>
          <div className="text-sm text-gray-500">
            {new Date(contract.createdAt).toLocaleDateString('ro-RO')}
          </div>
        </div>
      )
    },
    {
      key: 'clientName',
      title: 'Client',
      sortable: true,
      render: (value) => (
        <span className="text-sm text-gray-900">{value}</span>
      )
    },
    {
      key: 'status',
      title: 'Status',
      sortable: true,
      render: (value, contract) => (
        <StatusBadge status={contract.statusText} />
      )
    },
    {
      key: 'dueDate',
      title: 'Valabilitate',
      sortable: true,
      render: (value, contract) => (
        <div>
          <div className="text-sm text-gray-900">
            {new Date(value).toLocaleDateString('ro-RO')}
          </div>
          <div className={`text-xs ${contract.daysUntilExpiry < 0
            ? 'text-red-600'
            : contract.daysUntilExpiry < 30
              ? 'text-orange-600'
              : 'text-gray-500'
            }`}>
            {contract.daysUntilExpiry < 0
              ? `Expirat cu ${Math.abs(contract.daysUntilExpiry)} zile`
              : `${contract.daysUntilExpiry} zile rămase`
            }
          </div>
        </div>
      )
    },
    {
      key: 'totalAmount',
      title: 'Valoare',
      sortable: true,
      render: (value, contract) => (
        <div className="text-sm text-gray-900">
          {value?.toLocaleString('ro-RO')} {contract.currency}
        </div>
      )
    },
    {
      key: 'servicesCount',
      title: 'Servicii',
      sortable: true,
      render: (value) => (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
          {value}
        </span>
      )
    },
    {
      key: 'actions',
      title: 'Acțiuni',
      sortable: false,
      render: (_, contract) => (
        <div className="flex space-x-1">
          <Button
            size="sm"
            variant="outline"
            icon={Eye}
            onClick={() => handleViewContract(contract)}
            title="Vizualizează"
          />
          {contract.status === 'Draft' && (
            <Button
              size="sm"
              variant="outline"
              icon={FileText}
              onClick={() => handleGeneratePdf(contract.id)}
              loading={generatePdfMutation.isLoading}
              title="Generează PDF"
            />
          )}
          {(contract.status === 'Generated' || contract.status === 'Draft') && (
            <Button
              size="sm"
              variant="outline"
              icon={Send}
              onClick={() => handleSendContract(contract.id)}
              loading={sendContractMutation.isLoading}
              title="Trimite Contract"
            />
          )}
          <Button
            size="sm"
            variant="outline"
            icon={Download}
            onClick={() => handleDownloadContract(contract)}
            loading={downloadContractMutation.isLoading}
            title="Descarcă PDF"
          />
        </div>
      )
    }
  ];

  const isLoading = contractsLoading || clientsLoading || servicesLoading || templatesLoading;

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <LoadingSpinner size="lg" text="Se încarcă contractele..." />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Navigation
        title="Gestiune Contracte"
        subtitle={`${contracts.length} contracte în sistem`}
        actions={[
          {
            label: 'Export',
            icon: Download,
            variant: 'outline',
            onClick: handleExportContracts
          },
          {
            label: 'Contract Nou',
            icon: Plus,
            variant: 'primary',
            onClick: () => {
              setEditingContract(null);
              setShowContractForm(true);
            }
          }
        ]}
      />

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 rounded-lg">
              <FileText className="w-5 h-5 text-blue-600" />
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-600">Active</p>
              <p className="text-lg font-semibold text-gray-900">
                {contracts.filter(c => !c.isExpired && c.statusText !== 'Terminated').length}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 rounded-lg">
              <FileText className="w-5 h-5 text-green-600" />
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-600">Semnate</p>
              <p className="text-lg font-semibold text-gray-900">
                {contracts.filter(c => c.statusText === 'Semnat').length}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center">
            <div className="p-2 bg-yellow-100 rounded-lg">
              <Calendar className="w-5 h-5 text-yellow-600" />
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-600">În Așteptare</p>
              <p className="text-lg font-semibold text-gray-900">
                {contracts.filter(c => c.statusText === 'Trimis' || c.statusText === 'Accesat').length}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center">
            <div className="p-2 bg-red-100 rounded-lg">
              <AlertCircle className="w-5 h-5 text-red-600" />
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-600">Expirate</p>
              <p className="text-lg font-semibold text-gray-900">
                {contracts.filter(c => c.isExpired).length}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="space-y-4">
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="flex-1">
              <SearchBar
                placeholder="Căutați după număr contract sau client..."
                value={searchTerm}
                onChange={setSearchTerm}
                onClear={() => setSearchTerm('')}
              />
            </div>
            <div className="flex space-x-3">
              <Button 
                variant="outline" 
                icon={Filter} 
                size="sm"
                onClick={() => setShowFilters(!showFilters)}
              >
                {showFilters ? 'Ascunde filtre' : 'Mai multe filtre'}
              </Button>
            </div>
          </div>

          {/* Extended Filters */}
          {showFilters && (
            <div className="pt-4 border-t border-gray-200">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Status
                  </label>
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="all">Toate statusurile</option>
                    <option value="ciornă">Ciornă</option>
                    <option value="generat">Generat</option>
                    <option value="trimis">Trimis</option>
                    <option value="accesat">Accesat</option>
                    <option value="semnat">Semnat</option>
                    <option value="expirat">Expirat</option>
                    <option value="reziliat">Reziliat</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    An
                  </label>
                  <select
                    value={selectedYear}
                    onChange={(e) => setSelectedYear(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="all">Toti anii</option>
                    {availableYears.map(year => (
                      <option key={year} value={year}>{year}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Județ
                  </label>
                  <select
                    value={selectedCounty}
                    onChange={(e) => setSelectedCounty(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="all">Toate județele</option>
                    {availableCounties.map(county => (
                      <option key={county} value={county}>{county}</option>
                    ))}
                  </select>
                </div>

                <div className="flex items-end">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setStatusFilter('all');
                      setSelectedYear('all');
                      setSelectedCounty('all');
                      setSearchTerm('');
                    }}
                    className="w-full"
                  >
                    Resetează filtre
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Grouped View by Year and County */}
      {selectedYear === 'all' && selectedCounty === 'all' && !searchTerm && (
        <div className="space-y-4">
          {Object.keys(groupedContracts).sort((a, b) => b - a).map(year => (
            <div key={year} className="bg-white rounded-lg shadow-sm border border-gray-200">
              <button
                onClick={() => toggleYearExpansion(year)}
                className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center space-x-3">
                  {expandedYears[year] ? (
                    <ChevronDown className="w-5 h-5 text-gray-500" />
                  ) : (
                    <ChevronRight className="w-5 h-5 text-gray-500" />
                  )}
                  <span className="text-lg font-semibold text-gray-900">{year}</span>
                  <span className="text-sm text-gray-500">
                    ({Object.values(groupedContracts[year]).flat().length} contracte)
                  </span>
                </div>
              </button>

              {expandedYears[year] && (
                <div className="px-4 pb-4 space-y-3">
                  {Object.keys(groupedContracts[year]).sort().map(county => (
                    <div key={county} className="border-l-2 border-blue-500 pl-4">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="font-medium text-gray-900">{county}</h4>
                        <span className="text-sm text-gray-500">
                          {groupedContracts[year][county].length} contracte
                        </span>
                      </div>
                      <div className="space-y-2">
                        {groupedContracts[year][county].slice(0, 3).map(contract => (
                          <div
                            key={contract.id}
                            className="flex items-center justify-between p-2 bg-gray-50 rounded hover:bg-gray-100 cursor-pointer"
                            onClick={() => handleViewContract(contract)}
                          >
                            <div className="flex-1">
                              <div className="text-sm font-medium text-gray-900">
                                {contract.number}
                              </div>
                              <div className="text-xs text-gray-500">
                                {contract.clientName}
                              </div>
                            </div>
                            <StatusBadge status={contract.statusText} />
                          </div>
                        ))}
                        {groupedContracts[year][county].length > 3 && (
                          <button
                            onClick={() => {
                              setSelectedYear(year);
                              setSelectedCounty(county);
                            }}
                            className="text-sm text-blue-600 hover:text-blue-800"
                          >
                            Vezi toate ({groupedContracts[year][county].length})
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Standard Table View (when filters are active) */}
      {(selectedYear !== 'all' || selectedCounty !== 'all' || searchTerm) && (
        <>
          <DataTable
            columns={columns}
            data={currentItems}
            loading={isLoading}
            emptyMessage="Nu au fost găsite contracte"
            sortBy={sortBy}
            sortDirection={sortDirection}
            onSort={handleSort}
            hoverable
          />

          {filteredContracts.length > 0 && (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              pageSize={pageSize}
              totalItems={filteredContracts.length}
              startItem={startItem}
              endItem={endItem}
              hasNextPage={hasNextPage}
              hasPreviousPage={hasPreviousPage}
              onPageChange={goToPage}
              onPageSizeChange={changePageSize}
              pageSizeOptions={pageSizeOptions}
              itemName="contracte"
            />
          )}
        </>
      )}

      {/* Contract Form Modal */}
      <ContractForm
        contract={editingContract}
        clients={clients}
        services={services}
        templates={templates}
        isOpen={showContractForm}
        onClose={() => {
          setShowContractForm(false);
          setEditingContract(null);
        }}
        onSubmit={handleContractSubmit}
        loading={createContractMutation.isLoading || updateContractMutation.isLoading}
      />
    </div>
  );
};

export default ContractsPage;